import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toastApiError } from '@/core/api';
import { usePreferences, useUpdatePreferences } from '../queries';
import { DIGEST_MODES, type DigestMode } from '../types';

/**
 * The caller's own delivery switches (`/api/notification-preferences/`).
 *
 * Per membership, not per user: a parent at one school and a teacher at
 * another want different things, and the backend models it that way.
 */
export function NotificationPreferencesPanel() {
  const { t } = useTranslation(['notifications']);
  const preferences = usePreferences();
  const update = useUpdatePreferences();

  async function save(input: Parameters<typeof update.mutateAsync>[0]) {
    try {
      await update.mutateAsync(input);
      toast.success(t('notifications:preferences.saved'));
    } catch (e) {
      toastApiError(e);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('notifications:preferences.title')}</CardTitle>
        <CardDescription>{t('notifications:preferences.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {preferences.isLoading || !preferences.data ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <div className="flex items-start gap-2">
              <input
                id="pref-in-app"
                type="checkbox"
                className="border-input accent-primary mt-1 size-4 rounded border"
                checked={preferences.data.in_app}
                disabled={update.isPending}
                onChange={event => void save({ in_app: event.target.checked })}
              />
              <div>
                <Label htmlFor="pref-in-app">{t('notifications:preferences.inApp')}</Label>
                <p className="text-muted-foreground text-xs">
                  {t('notifications:preferences.inAppHint')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <input
                id="pref-email"
                type="checkbox"
                className="border-input accent-primary mt-1 size-4 rounded border"
                checked={preferences.data.email}
                disabled={update.isPending}
                onChange={event => void save({ email: event.target.checked })}
              />
              <div>
                <Label htmlFor="pref-email">{t('notifications:preferences.email')}</Label>
                <p className="text-muted-foreground text-xs">
                  {t('notifications:preferences.emailHint')}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pref-digest">{t('notifications:preferences.digest')}</Label>
              <Select
                value={preferences.data.digest}
                disabled={update.isPending}
                onValueChange={value => void save({ digest: value as DigestMode })}
              >
                <SelectTrigger id="pref-digest" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIGEST_MODES.map(mode => (
                    <SelectItem key={mode} value={mode}>
                      {t(`notifications:preferences.digestModes.${mode}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {t('notifications:preferences.parentNote')}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
